import styled, { DefaultTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Spacer, VDBText } from "@components/atoms";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import React, { useMemo } from "react";
import RadioButton from "@components/atoms/RadioButton";
import { computePlanPrice } from "./utils";
import UserSelector from "./components/UserSelector";
import {
  addComma,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";

export type CardAction = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
};

export type CardProps = {
  title: string;
  description?: string;
  children?: React.ReactNode;
  actions?: CardAction[];
  checked?: boolean;
  onSelect?: () => void;
  item?: any;
  additionalUsers: number;
  setAdditionalUsers: (value: number) => void;
  disabled?: boolean;
  type: "renewal" | "purchase";
};

const Card: React.FC<CardProps> = ({
  title,
  description,
  checked,
  onSelect,
  item,
  additionalUsers,
  setAdditionalUsers,
  disabled,
  type,
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const totals = useMemo(
    () => computePlanPrice(item, additionalUsers),
    [item, additionalUsers]
  );

  const computePlanPriceComponent = (plan: any) => {
    const basePrice = plan?.base_price ?? 0;
    const discountPercent = plan?.discount_percent ?? 0;
    const discountedPrice = basePrice * (1 - discountPercent / 100);
    return discountedPrice;
  };

  return (
    <CardContainer checked={!!checked} onPress={onSelect} disabled={disabled}>
      <TopContainer checked={!!checked}>
        <HeaderRow>
          <RadioButtonContainer>
            <RadioButton
              checked={!!checked}
              onPress={onSelect ?? (() => {})}
              margin="0"
              hideLabel={true}
            />
            <Spacer size={SpaceType.Basic} />
            <VDBText
              type={typography["Lead-M"]}
              color={theme[checked ? "core-05" : "core-06"]}
            >
              {title}
            </VDBText>
          </RadioButtonContainer>
          {item.plan_info.map(
            (plan: any) =>
              plan?.discount_percent && (
                <DiscountContainer>
                  <VDBText
                    type={typography["Minor-M"]}
                    color={theme["success-01"]}
                  >
                    {t("managePlan.popUp.save.text", "Save ")}
                    {plan?.discount_percent}%
                  </VDBText>
                </DiscountContainer>
              )
          )}
        </HeaderRow>
        <Spacer size={SpaceType.ExtraSmall} />
        <VDBText type={typography["Minor-M"]} color={theme["success-01"]}>
          {description && !item.disablePromotionText
            ? description
            : item?.plan_description
            ? item?.plan_description
            : " "}
        </VDBText>
      </TopContainer>
      <PlanInfoContainer>
        {item.plan_info.map((plan: any, index: number) => (
          <>
            <PriceContainer>
              <PlanInfoRow>
                {plan?.discount_percent && (
                  <>
                    <VDBText
                      type={typography["Body-R"]}
                      color={theme["error-01"]}
                      textDecoration="line-through"
                    >
                      {plan?.currency_symbol}
                      {plan?.base_price / (item?.plan_type ?? 1)}
                    </VDBText>
                    <Spacer size={SpaceType.Small} />{" "}
                  </>
                )}
                <VDBText type={typography["H1-B"]} color={theme["core-05"]}>
                  {plan?.currency_symbol}
                  {computePlanPriceComponent(plan) / (item?.plan_type ?? 1)}
                </VDBText>
                <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                  {t("managePlan.popUp.basePriceCard.text", " / year")}
                </VDBText>
              </PlanInfoRow>

              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                {t("managePlan.popUp.includeUser.text", "includes")}{" "}
                {item?.base_included_users ?? 0}{" "}
                {t("managePlan.popUp.user.text", "user")}
              </VDBText>
            </PriceContainer>
            <Spacer size={SpaceType.ExtraSmall} />
            {(item?.plan_type ?? 1) > 1 ? (
              <VDBText type={typography["Minor-R"]} color={theme["core-05"]}>
                {plan?.currency_symbol}
                {computePlanPriceComponent(plan)}
                {" billed upfront for " + (item?.plan_type ?? 1) + " years"}
              </VDBText>
            ) : (
              <VDBText type={typography["Minor-R"]} color={theme["core-05"]}>
                {" "}
              </VDBText>
            )}

            {index !== item.plan_info.length - 1 && (
              <Spacer size={SpaceType.Basic} />
            )}
          </>
        ))}
      </PlanInfoContainer>
      <AdditionalUserContainer>
        <UserSelector
          id={item.plan_id}
          pricePerAdditionalUser={totals?.addUsersCost ?? 0}
          value={additionalUsers}
          onChange={setAdditionalUsers}
          item={item}
          disabled={!checked}
        />
      </AdditionalUserContainer>
      <AdditionalUserSubtotalContainer>
        <AdditionalUserSubtotalContainerRow>
          <VDBText type={typography["Minor-R"]} color={theme["core-05"]}>
            {t(
              "managePlan.popUp.additionalUserSubtotal.text",
              "Additional users subtotal"
            )}
            {` (${item?.plan_type ?? 1} ${
              "year" + ((item?.plan_type ?? 1) > 1 ? "s" : "")
            })`}
          </VDBText>
          <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
            {item?.additional_user_plan?.currency_symbol}
            {addComma(
              formatNumberWithPrecision(totals?.addUsersCost ?? 0, {
                precision: 0,
              })
            )}
          </VDBText>
        </AdditionalUserSubtotalContainerRow>
        <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
          {`${additionalUsers} users x ${
            item?.additional_user_plan?.currency_symbol +
            (item?.additional_user_plan?.discounted_price ??
              item?.additional_user_plan?.base_price)
          } x ${item?.plan_type ?? 1} ${
            "year" + ((item?.plan_type ?? 1) > 1 ? "s" : "")
          }`}
        </VDBText>
      </AdditionalUserSubtotalContainer>
      <BottomContainer>
        <BottomContainerRow>
          <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
            {type === "renewal"
              ? t("managePlan.popUp.renewalTotal.text", "Renewal Total")
              : t("managePlan.popUp.total.text", "Total")}
            {` (${item?.plan_type ?? 1} year${
              (item?.plan_type ?? 1) > 1 ? "s" : ""
            })`}
          </VDBText>
          <VDBText type={typography["H1-B"]} color={theme["core-05"]}>
            {item?.additional_user_plan?.currency_symbol}{" "}
            {addComma(
              formatNumberWithPrecision(totals?.subTotal ?? 0, { precision: 0 })
            )}
          </VDBText>
        </BottomContainerRow>
        <Spacer size={SpaceType.ExtraSmall} />
        {totals?.subTotalSavings && item?.plan_type > 1 ? (
          <VDBText type={typography["Minor-M"]} color={theme["success-01"]}>
            {t(
              "managePlan.popUp.savings.text",
              "You save {{amount}} with the {{planTerm}} plan"
            )
              .replace(
                "{{amount}}",
                `${item?.additional_user_plan?.currency_symbol}${addComma(
                  formatNumberWithPrecision(totals?.subTotalSavings ?? 0, {
                    precision: 0,
                  })
                )}`
              )
              .replace("{{planTerm}}", `${item?.plan_type ?? 1}-year`)}
          </VDBText>
        ) : (
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {" "}
          </VDBText>
        )}
      </BottomContainer>
    </CardContainer>
  );
};

const CardContainer = styled.Pressable<{ checked: boolean }>`
  background-color: ${({
    theme,
    checked,
  }: {
    theme: DefaultTheme;
    checked: boolean;
  }) => theme[checked ? "core-01" : "core-02"]};
  border-radius: ${SpaceType.Basic}px;
  border-width: 2px;
  border-color: ${({
    theme,
    checked,
  }: {
    theme: DefaultTheme;
    checked: boolean;
  }) => theme[checked ? "main-06" : "core-02"]};
`;

const HeaderRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const RadioButtonContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TopContainer = styled.View<{ checked: boolean }>`
  padding: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const BottomContainer = styled.View`
  padding: ${SpaceType.Extra}px;
`;

const BottomContainerRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PlanInfoContainer = styled.View`
  padding: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const PriceContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PlanInfoRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const DiscountContainer = styled.View`
  padding-vertical: 3px;
  padding-horizontal: ${SpaceType.ExtraSmall}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["success-02"]};
  border-radius: ${SpaceType.ExtraSmall}px;
`;

const AdditionalUserContainer = styled.View`
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const AdditionalUserSubtotalContainer = styled.View`
  padding: ${SpaceType.Basic}px ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const AdditionalUserSubtotalContainerRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export default Card;
